import { useCallback, useRef, useState } from "react";

/**
 * Custom hook for a toast that auto-hides after a fixed duration.
 * @param {number} [durationMs] - How long the toast stays visible. Defaults to 3000.
 * @returns The `show` flag, current `message`, and `showToast`/`hideToast` helpers.
 * @example
 * const { show, message, showToast, hideToast } = useTimedToast();
 * showToast("Saved!");
 */
export const useTimedToast = (durationMs: number = 3000) => {
  const [show, setShow] = useState(false);
  const [message, setMessage] = useState("");
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const showToast = useCallback(
    (nextMessage: string) => {
      setMessage(nextMessage);
      setShow(true);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => setShow(false), durationMs);
    },
    [durationMs],
  );

  const hideToast = useCallback(() => setShow(false), []);

  return { show, message, showToast, hideToast };
};
